import type { FormFields, FormErrors } from '../formFields'

import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useFormApi } from '@workwave-tidal/form-fairy'

import { useIsUnmounted, useNotification } from '@/hooks'
import { deleteTenantSource } from '@/features/domain/gps'
import { selectIsTelematicsUnavailable } from '@/features/domain/account'
import { useAppDispatch } from '@/store'

import { useController } from './useController'
import { useTexts } from '../useTexts'

export function useOnSubmit(setFirstSubmitDone: (value: boolean) => void) {
  const toast = useNotification()
  const formApi = useFormApi<FormFields, FormErrors>()
  const dispatch = useAppDispatch()
  const {
    update,
    close,
    data: { tenantSource },
  } = useController()
  const isUnmounted = useIsUnmounted()
  const isTelematicsUnavailable = useSelector(selectIsTelematicsUnavailable)
  const texts = useTexts()

  return useCallback(async () => {
    try {
      setFirstSubmitDone(true)
      formApi.setSubmitting(true)
      update({ status: 'submitting' })

      await formApi.waitForValidation()

      const valid =
        formApi.getMeta().status === 'indeterminate'
          ? await formApi.validate()
          : formApi.getMeta().status === 'valid'

      // if the component has been unmounted during the async operation stop here
      if (isUnmounted()) return

      if (!valid) {
        formApi.setSubmitting(false)
        update({ status: 'ready' })
        return
      }

      const thunkResult = await dispatch(deleteTenantSource(tenantSource.id))

      if (!deleteTenantSource.fulfilled.match(thunkResult)) {
        throw new Error(thunkResult.payload?.message ?? texts.serviceUnavailableGenericError)
      }

      formApi.setSubmitting(false)
      close?.()
    } catch (error) {
      formApi.setSubmitting(false)
      update({ status: 'ready' })
      const errorMessage = isTelematicsUnavailable
        ? texts.serviceUnavailableGenericError
        : error instanceof Error
          ? error.message
          : texts.serviceUnavailableGenericError
      toast.error(errorMessage)
    }
  }, [
    formApi,
    toast,
    update,
    close,
    dispatch,
    isUnmounted,
    setFirstSubmitDone,
    tenantSource,
    texts,
    isTelematicsUnavailable,
  ])
}
